/**
 * DaybreakBanner — a non-blocking invitation to start the morning
 * ritual. Replaces the auto-popping modal that previously intercepted
 * pointer events on the topbar (testing-agent iteration 136 flagged
 * this as a UX nit).
 *
 * Lives at the top of TodayView. Only renders when `pending=true`,
 * i.e. the user hasn't completed or skipped today's ritual yet.
 *
 * Three affordances:
 *   - "Start your day" CTA → opens the existing DaybreakModal
 *   - Sun-glyph card showing date & a soft prompt
 *   - "Skip for today" → dismisses for the rest of today, server-side
 */
import { Sun, ArrowRight, X } from 'lucide-react';
import { format } from 'date-fns';
import { formatDate } from '../../utils/locale';

export default function DaybreakBanner({ pending, onStart, onDismiss }) {
  if (!pending) return null;

  const today = new Date();
  const niceDate = `${format(today, 'EEEE')}, ${formatDate(today, { includeYear: false })}`;

  return (
    <div
      data-testid="daybreak-banner"
      className="mx-4 mt-3 rounded-2xl px-4 py-3 flex items-center gap-3"
      style={{
        background: 'linear-gradient(135deg, var(--pd-panel-2) 0%, var(--pd-panel-1) 100%)',
        border: '1px solid var(--pd-border)',
        boxShadow: 'var(--pd-shadow-1)',
      }}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{
          background: 'var(--pd-accent-soft)',
          color: 'var(--pd-accent)',
        }}
      >
        <Sun className="w-[18px] h-[18px]" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold" style={{ color: 'var(--pd-text)' }}>
          Set the tone for {niceDate}
        </div>
        <div className="text-[11px] mt-0.5" style={{ color: 'var(--pd-subtle)' }}>
          2-minute ritual — commit to today&apos;s outcomes, energy check, primary focus.
        </div>
      </div>

      <button
        type="button"
        data-testid="daybreak-banner-start"
        onClick={onStart}
        className="px-3 py-1.5 rounded-lg text-[12px] font-semibold flex items-center gap-1.5 transition-all pd-btn-press"
        style={{
          background: 'var(--pd-accent)',
          color: 'var(--pd-canvas)',
          border: '1px solid var(--pd-accent)',
        }}
      >
        Start your day
        <ArrowRight className="w-3 h-3" />
      </button>

      <button
        type="button"
        data-testid="daybreak-banner-dismiss"
        onClick={onDismiss}
        className="px-2 py-1 text-[11px] transition-colors hidden sm:inline-block"
        style={{ color: 'var(--pd-subtle)' }}
        title="Skip the ritual for today"
      >
        Skip for today
      </button>

      {/* Mobile-only X — `Skip for today` is text-link only and hidden
          on small screens, so we surface a minimal icon affordance.
          Same handler, same `data-testid` family so tests can find it. */}
      <button
        type="button"
        data-testid="daybreak-banner-dismiss-mobile"
        onClick={onDismiss}
        className="p-1.5 rounded-md sm:hidden transition-colors"
        style={{ color: 'var(--pd-subtle)' }}
        title="Skip for today"
        aria-label="Skip Daybreak for today"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
